import {MutationCache, QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {ApiError} from 'client';
import {
  is5xxError,
  log5xxError,
  logApiError,
  logAxiosError,
  shouldFilterFromSentry,
} from 'helpers/logging';
import {useEffect} from 'react';
import {useLocation} from 'react-router';

export const queryClient = new QueryClient({
  defaultOptions: {
    // Apply to all queries, override if needed
    queries: {
      // By default if a request fails, react-query will retry it 3 times.
      retry: false,
      // If you want to refetch data, add it to the queryKey array below
      staleTime: 1000 * 60 * 30, // 30 minutes
    },
  },
  // Set up global error listeners for React Query to catch mutation errors
  mutationCache: new MutationCache({
    onError: (error) => {
      // Skip logging for user-initiated cancellations and network errors
      if (shouldFilterFromSentry(error)) {
        return;
      }
      if (error instanceof ApiError) {
        logApiError(error, {
          component: 'react_query_global',
          userAction: 'mutation_error',
        });
      }
      if (error instanceof AxiosError) {
        logAxiosError(error, {
          component: 'react_query_global',
          userAction: 'mutation_error',
        });
      }
    },
  }),
});

// Set up global error listeners for React Query to catch 5xx errors
queryClient.getQueryCache().subscribe((event) => {
  if (event.type === 'updated' && event.query.state.error) {
    const error = event.query.state.error;
    // Skip logging for user-initiated cancellations and network errors
    if (shouldFilterFromSentry(error)) {
      return;
    }
    if (is5xxError(error)) {
      log5xxError(error, {
        component: 'react_query_global',
        userAction: 'query_error',
        url: event.query.queryKey.join('_'),
      });
    }
  }
});

export const QueryClientWrapper = ({children}: {children: React.ReactNode}) => {
  // Invalidate queries when navigating to a new page
  const location = useLocation();
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['notifications'],
    });
  }, [location.pathname]);

  useEffect(() => {
    const handleOnline = () => {
      queryClient.invalidateQueries({queryKey: ['workstreams'], exact: false});
      queryClient.invalidateQueries({queryKey: ['workstream'], exact: false});
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};
